import { WindowsHSMCertType } from './base.js';
/*
  Code signing certificate references on an app document. Only Cloud Functions
  write them (firestore.rules protects the fields), so the dashboard and the
  Functions share these helpers to agree on what a write changes.
*/
export const windowsCertTargets = ['windows'];
export const macCertTargets = [
    'mac',
    'mac-installer',
    'mas',
    'mas-dev',
    'mas-installer',
];
/** The Windows certificate an app signs with for a target, if any. */
export const getInstalledWindowsCertName = (app, target) => {
    var _a;
    const signingConfig = app.customWindowsCodeSign;
    return (((_a = app.windowsHsmCertNames) === null || _a === void 0 ? void 0 : _a[target]) ||
        (target === 'windows' && signingConfig && 'hsmCertName' in signingConfig
            ? signingConfig.hsmCertName
            : ''));
};
export const getWindowsEVRequestHistoryEntry = (app, name) => {
    var _a, _b, _c;
    const onboarding = (_a = app.meta) === null || _a === void 0 ? void 0 : _a.windowsEVOnboarding;
    return {
        createdAt: ((_c = (_b = onboarding === null || onboarding === void 0 ? void 0 : onboarding.requestHistory) === null || _b === void 0 ? void 0 : _b[name]) === null || _c === void 0 ? void 0 : _c.createdAt) ||
            ((onboarding === null || onboarding === void 0 ? void 0 : onboarding.tempHSMCertName) === name
                ? onboarding.tempHSMCertCreatedAt
                : 0) ||
            0,
    };
};
export const getWindowsEVRequestReset = (app) => {
    var _a, _b;
    const name = (_b = (_a = app.meta) === null || _a === void 0 ? void 0 : _a.windowsEVOnboarding) === null || _b === void 0 ? void 0 : _b.tempHSMCertName;
    return Object.assign(Object.assign({}, (name
        ? {
            requestHistory: {
                [name]: getWindowsEVRequestHistoryEntry(app, name),
            },
        }
        : {})), { hasGeneratedCert: false, hasUploadedCert: false, tempHSMCertCreatedAt: 0, tempHSMCertName: '' });
};
export const getRetiredCertificate = (app, hsmCertName) => {
    var _a, _b;
    return ({
        createdAt: getWindowsEVRequestHistoryEntry(app, hsmCertName).createdAt,
        hsmCertName,
        retireRequest: ((_b = (_a = app.meta) === null || _a === void 0 ? void 0 : _a.windowsEVOnboarding) === null || _b === void 0 ? void 0 : _b.tempHSMCertName) === hsmCertName,
    });
};
/**
 * Metadata retained when an installed certificate is replaced or deleted: its
 * reference stays in request history, and its request is retired when it is
 * still the tracked one. A separate unfinished renewal must survive either.
 */
const getRetiredCertificateFields = ({ createdAt, hsmCertName, retireRequest, }) => (Object.assign({ [`meta.windowsEVOnboarding.requestHistory.${hsmCertName}`]: { createdAt } }, (retireRequest
    ? {
        'meta.windowsEVOnboarding.hasGeneratedCert': false,
        'meta.windowsEVOnboarding.hasUploadedCert': false,
        'meta.windowsEVOnboarding.tempHSMCertCreatedAt': 0,
        'meta.windowsEVOnboarding.tempHSMCertName': '',
    }
    : {})));
/**
 * The same retirement for a local copy of the app. A newer copy can arrive
 * after the change commits, so its request is retired only while that copy
 * still tracks it.
 */
export const getRetiredCertificateLocalMeta = ({ createdAt, hsmCertName, retireRequest }, current) => {
    var _a, _b;
    return ({
        windowsEVOnboarding: Object.assign(Object.assign({}, (retireRequest &&
            ((_b = (_a = current.meta) === null || _a === void 0 ? void 0 : _a.windowsEVOnboarding) === null || _b === void 0 ? void 0 : _b.tempHSMCertName) === hsmCertName
            ? getWindowsEVRequestReset(current)
            : {})), { requestHistory: { [hsmCertName]: { createdAt } } }),
    });
};
/**
 * What a certificate change does to an app, as persisted: `update` is the
 * Firestore update (with `deleteField` as the delete sentinel of the caller's
 * SDK), `fields` the resulting certificate fields, and `retired` the replaced
 * Windows certificate, if any.
 */
export function getCodeSigningCertChange(app, change, deleteField) {
    var _a;
    const { hsmCertName } = change;
    const fields = {
        customMacCodeSign: app.customMacCodeSign,
        customNotarization: app.customNotarization,
        customWindowsCodeSign: app.customWindowsCodeSign,
        macHsmCertNames: app.macHsmCertNames,
        windowsHsmCertNames: app.windowsHsmCertNames,
    };
    if (change.platform === 'windows') {
        const { target } = change;
        const previousName = getInstalledWindowsCertName(app, target);
        const retired = previousName && previousName !== hsmCertName
            ? getRetiredCertificate(app, previousName)
            : undefined;
        // Replace the whole configuration so old provider credentials (Azure
        // Trusted Signing) can't survive.
        const config = hsmCertName === null
            ? undefined
            : {
                certType: 'hsm',
                hsmCertName,
                hsmCertType: (_a = change.hsmCertType) !== null && _a !== void 0 ? _a : WindowsHSMCertType.file,
            };
        const names = Object.assign({}, app.windowsHsmCertNames);
        if (hsmCertName === null)
            delete names[target];
        else
            names[target] = hsmCertName;
        const isMainTarget = target === 'windows';
        return {
            fields: Object.assign(Object.assign(Object.assign({}, fields), (isMainTarget
                ? {
                    customWindowsCodeSign: config,
                }
                : {})), { windowsHsmCertNames: names }),
            retired,
            update: Object.assign(Object.assign(Object.assign({}, (isMainTarget
                ? { customWindowsCodeSign: config !== null && config !== void 0 ? config : deleteField }
                : {})), { [`windowsHsmCertNames.${target}`]: hsmCertName !== null && hsmCertName !== void 0 ? hsmCertName : deleteField, 'meta.isAppChanged': true }), (retired ? getRetiredCertificateFields(retired) : {})),
        };
    }
    const { target } = change;
    const names = Object.assign({}, app.macHsmCertNames);
    if (hsmCertName === null)
        delete names[target];
    else
        names[target] = hsmCertName;
    const macHsmCertNames = names;
    if (hsmCertName === null) {
        // Removing the main certificate also clears notarization.
        return target === 'mac'
            ? {
                fields: Object.assign(Object.assign({}, fields), { customMacCodeSign: undefined, customNotarization: undefined, macHsmCertNames }),
                update: {
                    'customMacCodeSign': deleteField,
                    'customNotarization': deleteField,
                    'macHsmCertNames.mac': deleteField,
                    // Signing changed, so the next build gets a new version.
                    'meta.isAppChanged': true,
                },
            }
            : {
                fields: Object.assign(Object.assign({}, fields), { macHsmCertNames }),
                update: {
                    [`macHsmCertNames.${target}`]: deleteField,
                    'meta.isAppChanged': true,
                },
            };
    }
    // Clears a legacy URL certificate's fields.
    const customMacCodeSign = {
        certName: null,
        certPassword: null,
        certUrl: null,
        type: 'hsm',
    };
    return {
        fields: Object.assign(Object.assign({}, fields), { customMacCodeSign, macHsmCertNames }),
        update: {
            [`macHsmCertNames.${target}`]: hsmCertName,
            customMacCodeSign,
            'meta.isAppChanged': true,
        },
    };
}
